import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Trophy, Crown, Medal, ArrowLeft } from 'lucide-react'
import { statsAPI } from '@/lib/api'
import PageHeader from '@/components/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

const SORTS = [
  { key: 'total_points',      label: 'Punten' },
  { key: 'tournaments_won',   label: 'Overwinningen' },
  { key: 'win_rate',          label: 'Win-rate' },
  { key: 'matches_won',       label: 'Matches gewonnen' },
  { key: 'tournaments_played',label: 'Deelnames' },
]

export default function TournamentsLeaderboardPage() {
  const [rows, setRows] = useState([])
  const [sortBy, setSortBy] = useState('total_points')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    statsAPI.getLeaderboard({ sortBy, limit: 100 })
      .then(r => setRows(r.data.leaderboard || []))
      .finally(() => setLoading(false))
  }, [sortBy])

  const podium = rows.slice(0, 3)
  const rest = rows.slice(3)

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Trophy}
        eyebrow="All-time · tournaments"
        title="Leaderboard"
        subtitle="Cumulatieve stats over alle toernooien heen. Klik een naam voor het profiel."
        actions={<Button asChild variant="outline"><Link to="/tournaments"><ArrowLeft className="h-4 w-4 mr-2" />Terug</Link></Button>}
      />

      <div className="flex gap-2 flex-wrap">
        {SORTS.map(s => (
          <Button key={s.key} size="sm"
                  variant={sortBy === s.key ? 'default' : 'outline'}
                  onClick={() => setSortBy(s.key)}>
            {s.label}
          </Button>
        ))}
      </div>

      {loading && <div className="text-sm text-muted-foreground">Laden…</div>}

      {!loading && podium.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {podium.map((p, i) => {
            const tone = i === 0 ? { bg: 'bg-gold/8', border: 'border-gold', text: 'text-gold', Icon: Crown }
                        : i === 1 ? { bg: 'bg-muted/40', border: 'border-muted-foreground/40', text: 'text-muted-foreground', Icon: Medal }
                                   : { bg: 'bg-peach/8', border: 'border-peach', text: 'text-peach', Icon: Medal }
            const { Icon } = tone
            return (
              <Card key={p.user_id} className={`border ${tone.border}`}>
                <CardContent className="p-4 flex flex-col items-center text-center">
                  <Icon className={`h-8 w-8 ${tone.text} mb-2`} />
                  <div className={`font-mono text-[10px] uppercase tracking-widest ${tone.text}`}>#{i + 1}</div>
                  <div className="text-xl font-bold mt-1">{p.display_name || p.username}</div>
                  <div className="text-3xl font-bold tabular-nums text-acid mt-2">{formatSortValue(p, sortBy)}</div>
                  <div className="text-xs text-muted-foreground mt-2">
                    {p.tournaments_won} × 1e · {p.tournaments_played} deelnames
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {!loading && rest.length > 0 && (
        <Card>
          <CardContent className="p-0 divide-y divide-border/40">
            {rest.map(p => (
              <div key={p.user_id} className="grid grid-cols-[3rem_1fr_auto_auto_auto] items-center gap-3 px-4 py-2">
                <div className="font-mono text-sm text-muted-foreground text-center tabular-nums">#{p.rank}</div>
                <div className="font-medium truncate">{p.display_name || p.username}</div>
                <div className="text-xs font-mono text-muted-foreground text-right w-24">
                  {p.tournaments_won}W / {p.tournaments_played}
                </div>
                <div className="text-xs font-mono text-muted-foreground text-right w-16">
                  {(p.win_rate * 100).toFixed(0)}%
                </div>
                <div className="font-mono text-sm tabular-nums text-acid w-20 text-right">
                  {formatSortValue(p, sortBy)}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {!loading && rows.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            Nog geen stats. Draai eerst een paar toernooien.
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function formatSortValue(p, sortBy) {
  if (sortBy === 'win_rate') return `${(p.win_rate * 100).toFixed(0)}%`
  return p[sortBy] ?? 0
}
